import { BigNumber } from 'bignumber.js';
import TokenAmount from '@/shared/utils/TokenAmount';

type OperatorStats = {
  numberOfNodes: number;
  totalStake: TokenAmount;
  totalDelegation: TokenAmount;
};

export const FALLBACK_ADDTL_DELEGATION = TokenAmount.fromWholeUnits(10000, 'Flip'); // 10K
const SECONDS_PER_DAY = 24 * 60 * 60;
export const BLOCK_TIME = 6;

// bid_per_node = (operator_total_stake + operator_total_delegation) / number_of_nodes;
export const adjustMinimumActiveBid = (
  operator: OperatorStats,
  additionalDelegation: TokenAmount,
  minActiveBid: TokenAmount | undefined,
) => {
  const { numberOfNodes, totalStake, totalDelegation } = operator;

  if (!minActiveBid || minActiveBid.eq(0)) return null;

  const total = totalStake.add(totalDelegation);
  const bidPerNode = total.value.dividedBy(numberOfNodes);

  if (bidPerNode.eq(minActiveBid.value)) {
    return TokenAmount.fromAsset(
      total.add(additionalDelegation).value.dividedBy(numberOfNodes),
      'Flip',
    );
  }

  return minActiveBid;
};

// operator_portion_of_mab_pct = operator_total_stake / (number_of_nodes * minimum_active_bid)
export const calculateOperatorPortion = (
  operatorTotalStake: TokenAmount,
  numberOfNodes: number,
  minActiveBid: TokenAmount,
): BigNumber => {
  const minActiveBidPerNode = minActiveBid.value.multipliedBy(numberOfNodes);
  return operatorTotalStake.value.dividedBy(minActiveBidPerNode);
};

// rewards_per_day = seconds_per_day / block_time / authority_set_size * number_of_nodes * block_reward
export const calculateRewardsPerDay = (
  numberOfNodes: number,
  blockReward: TokenAmount | undefined,
  authoritySetSize: number | undefined,
): TokenAmount => {
  const reward = blockReward?.value ?? new BigNumber(0);

  if (!authoritySetSize || authoritySetSize === 0 || reward.isZero()) {
    return TokenAmount.fromAsset(0, 'Flip');
  }

  const slotsPerDay = new BigNumber(SECONDS_PER_DAY)
    .dividedBy(BLOCK_TIME)
    .dividedBy(authoritySetSize)
    .multipliedBy(numberOfNodes);

  return TokenAmount.fromAsset(slotsPerDay.multipliedBy(reward), 'Flip');
};

export const calculateDelegatorAndOperatorDailyRewards = ({
  dailyRewards,
  operatorPortionOfMabPct,
  commissionBps,
}: {
  dailyRewards: TokenAmount;
  operatorPortionOfMabPct: BigNumber;
  commissionBps: number;
}) => {
  const operatorDailyRewardsFromStake = dailyRewards.mul(operatorPortionOfMabPct);
  const delegatorDailyRewardsBeforeCommission = dailyRewards.sub(operatorDailyRewardsFromStake);

  const operatorDailyRewardFromCommission = delegatorDailyRewardsBeforeCommission
    .mul(commissionBps)
    .div(10_000);
  return {
    delegator: delegatorDailyRewardsBeforeCommission.sub(operatorDailyRewardFromCommission),
    operator: operatorDailyRewardFromCommission.add(operatorDailyRewardsFromStake),
  };
};

export function calculateDelegatorApy(
  auction: { blockReward: TokenAmount; minActiveBid: TokenAmount | undefined } | null,
  additionalDelegation: TokenAmount,
  numberOfNodes: number,
  operatorTotalStake: TokenAmount,
  operatorTotalDelegation: TokenAmount,
  commissionBps: number,
  authoritySetSize: number | undefined,
): number {
  if (
    operatorTotalStake.value.dividedBy(numberOfNodes).gt(auction?.minActiveBid?.value ?? Infinity)
  ) {
    return 0;
  }

  const dailyRewards = calculateRewardsPerDay(
    numberOfNodes,
    auction?.blockReward,
    authoritySetSize,
  );

  const minActiveBid = adjustMinimumActiveBid(
    {
      numberOfNodes,
      totalStake: operatorTotalStake,
      totalDelegation: operatorTotalDelegation,
    },
    additionalDelegation,
    auction?.minActiveBid,
  );

  if (!minActiveBid || numberOfNodes === 0) return 0;

  const operatorPortionOfMabPct = calculateOperatorPortion(
    operatorTotalStake,
    numberOfNodes,
    minActiveBid,
  );

  const { delegator: delegatorDailyReward } = calculateDelegatorAndOperatorDailyRewards({
    dailyRewards,
    operatorPortionOfMabPct,
    commissionBps,
  });

  const delegatorStakeAndNewDelegation = operatorTotalDelegation.add(additionalDelegation);

  if (delegatorStakeAndNewDelegation.eq(0)) return 0;

  const delegatorApy = delegatorDailyReward.value
    .times(365)
    .dividedBy(delegatorStakeAndNewDelegation.value)
    .toNumber();

  return delegatorApy;
}

export const calculateOperatorDailyCommission = (
  auction: { blockReward: TokenAmount; minActiveBid: TokenAmount | undefined } | null,
  additionalDelegation: TokenAmount,
  numberOfNodes: number,
  operatorTotalStake: TokenAmount,
  operatorTotalDelegation: TokenAmount,
  commissionBps: number,
  authoritySetSize: number | undefined,
): TokenAmount => {
  if (
    operatorTotalStake.value.dividedBy(numberOfNodes).gt(auction?.minActiveBid?.value ?? Infinity)
  ) {
    return TokenAmount.ZERO;
  }

  const dailyRewards = calculateRewardsPerDay(
    numberOfNodes,
    auction?.blockReward,
    authoritySetSize,
  );

  const minActiveBid = adjustMinimumActiveBid(
    {
      numberOfNodes,
      totalStake: operatorTotalStake,
      totalDelegation: operatorTotalDelegation,
    },
    additionalDelegation,
    auction?.minActiveBid,
  );

  if (!minActiveBid || numberOfNodes === 0) return TokenAmount.ZERO;

  const operatorPortionOfMabPct = calculateOperatorPortion(
    operatorTotalStake,
    numberOfNodes,
    minActiveBid,
  );

  const { operator: dailyCommission } = calculateDelegatorAndOperatorDailyRewards({
    dailyRewards,
    operatorPortionOfMabPct,
    commissionBps,
  });

  return dailyCommission;
};

export type DelegationApy =
  | { type: 'loading' }
  | { type: 'n/a' }
  | { type: 'apyBp'; apyBp: number }
  | { type: 'apyPct'; apyPct: number; observedHours?: number };

export const compareDelegationApy = (a: DelegationApy, b: DelegationApy): -1 | 0 | 1 => {
  if (a.type === 'loading' && b.type === 'loading') return 0;
  if (a.type === 'loading') return -1;
  if (b.type === 'loading') return 1;

  if (a.type === 'n/a' && b.type === 'n/a') return 0;
  if (a.type === 'n/a') return -1;
  if (b.type === 'n/a') return 1;

  const aApyPct = a.type === 'apyPct' ? a.apyPct : a.apyBp / 100;
  const bApyPct = b.type === 'apyPct' ? b.apyPct : b.apyBp / 100;

  if (aApyPct === bApyPct) return 0;
  return aApyPct < bApyPct ? -1 : 1;
};
